/*!
 * smile-facade.js — block Smile.io launcher load on non-loyalty pages.
 *
 * Background:
 *   Smile.io ships as a Shopify Theme App Extension. The extension
 *   block injects ONE script tag into `<head>` via `{{ content_for_header }}`:
 *
 *     <script src="https://cdn.shopify.com/extensions/<uuid>/<build>/assets/smile-loader.js"
 *             type="text/javascript" defer></script>
 *
 *   That `smile-loader.js`, once it executes, dynamically inserts 4
 *   more script tags into `<head>` for the actual Smile runtime:
 *     - js.smile.io/v1/smile-shopify.js                 (async)
 *     - js.smile.io/v1/smile-lite-*.js                  (defer, module)
 *     - js.smile.io/v1/smile-shopify-*.modern.js        (defer, module)
 *     - js.smile.io/v1/vendor-*.modern.js               (defer, module)
 *
 *   Cumulative: ~24 resource fetches, several hundred KB transferred,
 *   ~500-1500 ms of main-thread work for fetch wrapping + analytics
 *   bootstrap.
 *
 *   But the theme actually USES Smile in only ONE place:
 *   `templates/page.loyalth.json` (the loyalty page at
 *   /pages/the-enclave-loyalty-program), which has a `<a href="#smile-home">`
 *   join button that the Smile launcher needs to handle.
 *
 *   Everywhere else the rewards icon is just `<a href="/account">` and
 *   Smile loads, runs, and serves no user-facing purpose.
 *
 * History (why this file was rewritten 2026-05-07):
 *   The original facade used a MutationObserver alone with regexes that
 *   matched `js.smile.io/...` and `/extensions/<uuid>/.../smile...` —
 *   but the actual `smile-loader.js` URL is
 *   `/extensions/<uuid>/<build-stamp>/assets/smile-loader.js`. The
 *   build-stamp segment between the UUID and `assets/` is something
 *   like `2026-05-06-14-37-34-UTC--b0e153c` — it does NOT contain the
 *   string "smile". The previous regex `/extensions\/[a-f0-9-]+\/[^/]*smile/`
 *   required "smile" in the segment immediately after the UUID and so
 *   never matched. Result: smile-loader.js was never removed,
 *   smile-loader.js then injected the 4 runtime scripts dynamically,
 *   and __smileFacadeRemoved stayed at 0 on every page.
 *
 *   This rewrite:
 *     1. Fixes the regex so `smile-loader.js` matches.
 *     2. Adds a sync sweep at script-eval time so any Smile scripts
 *        already parsed into `<head>` are removed before they execute.
 *     3. Wraps `Node.prototype.appendChild` and `Node.prototype.insertBefore`
 *        to catch JS-injected Smile scripts (the 4 runtime ones that
 *        smile-loader.js *would* inject if it had executed). This is
 *        belt-and-suspenders — if smile-loader.js is removed before
 *        execution, those 4 are never appended. But if any escape, the
 *        appendChild override catches them.
 *     4. Keeps the MutationObserver as a fallback for edge cases.
 *     5. Does not auto-disconnect — Smile's runtime can attempt
 *        injections at any time during the page lifecycle, so we keep
 *        the guards active.
 *
 * Loyalty page exemption:
 *   `theme.liquid` sets `window.__loadSmileEagerly = true` BEFORE this
 *   script runs when `template == 'page.loyalth'`. We bail immediately
 *   in that case and Smile loads normally.
 *
 * Why this is safe:
 *   - Loyalty page (where Smile is needed) loads Smile normally.
 *   - On other pages: rewards icon is a plain `<a href="/account">`,
 *     no JS dependency. Theme-side reads of `window.Smile.customer.*`
 *     only happen in `loyalty_grid.liquid`, which is only on the
 *     loyalty page.
 *   - Every code path is wrapped in try/catch so a failure here cannot
 *     break the page.
 *   - Idempotent via window.__smileFacadeInit.
 *   - The appendChild override is scoped to checking the *src* of
 *     incoming SCRIPT nodes only — it never blocks other DOM ops.
 */

(function () {
  try {
    if (window.__smileFacadeInit) return;
    window.__smileFacadeInit = true;

    // Loyalty page sets this BEFORE this script runs (see theme.liquid).
    // If true: don't intercept, let Smile load normally.
    if (window.__loadSmileEagerly === true) return;

    // Match Smile's CDN host AND the Shopify Theme App Extension
    // smile-loader URL. These patterns intentionally match:
    //   - js.smile.io/...                              (the runtime CDN)
    //   - /extensions/<uuid>/<anything>/.../smile...   (the extension loader,
    //                                                   regardless of build-stamp
    //                                                   between UUID and asset)
    var SMILE_HOST_RE = /js\.smile\.io|smile-shopify|smile-lite|smile-loader|smile_loader|\/smile\./i;
    // Match the Shopify extensions path with `smile` *anywhere after the UUID*,
    // not just in the segment immediately after it. This is what the previous
    // regex got wrong.
    var SMILE_EXT_RE = /\/extensions\/[a-f0-9-]+\/.*smile/i;

    function isSmileScript(node) {
      try {
        if (!node || node.tagName !== 'SCRIPT') return false;
        // Use getAttribute('src') to read the original (unresolved) value
        // when available — node.src returns the resolved absolute URL.
        // Either should match, but the resolved form is what's most
        // consistently populated.
        var src = node.src || (node.getAttribute && node.getAttribute('src')) || '';
        if (!src) return false;
        return SMILE_HOST_RE.test(src) || SMILE_EXT_RE.test(src);
      } catch (e) {
        return false;
      }
    }

    var removed = 0;
    var blocked = 0;

    function recordRemoval(src) {
      removed++;
      try {
        window.__smileFacadeRemoved = removed;
        (window.__smileFacadeRemovedSrcs = window.__smileFacadeRemovedSrcs || []).push(src);
      } catch (e) { /* shrug */ }
    }

    function recordBlock(src) {
      blocked++;
      try {
        window.__smileFacadeBlocked = blocked;
        (window.__smileFacadeBlockedSrcs = window.__smileFacadeBlockedSrcs || []).push(src);
      } catch (e) { /* shrug */ }
    }

    // Initial counters so consumers always see them defined.
    try {
      window.__smileFacadeRemoved = 0;
      window.__smileFacadeBlocked = 0;
    } catch (e) { /* shrug */ }

    // Synchronous sweep: walk the current DOM and remove any Smile
    // scripts we find. Called both immediately and again at later
    // milestones (post-content_for_header, DOMContentLoaded, load).
    function sweep() {
      try {
        var scripts = document.querySelectorAll('script[src]');
        for (var i = 0; i < scripts.length; i++) {
          var s = scripts[i];
          if (isSmileScript(s) && s.parentNode) {
            var src = s.src;
            s.parentNode.removeChild(s);
            recordRemoval(src);
          }
        }
      } catch (e) { /* shrug */ }
    }
    // Expose so theme.liquid can call it inline immediately after
    // `{{ content_for_header }}` is parsed.
    try { window.__smileFacadeSweep = sweep; } catch (e) { /* shrug */ }

    // ----- MutationObserver: catch script tags as the parser adds them -----
    if (typeof MutationObserver !== 'undefined') {
      try {
        var observer = new MutationObserver(function (mutations) {
          try {
            for (var i = 0; i < mutations.length; i++) {
              var added = mutations[i].addedNodes;
              for (var j = 0; j < added.length; j++) {
                var node = added[j];
                if (isSmileScript(node) && node.parentNode) {
                  var src = node.src;
                  node.parentNode.removeChild(node);
                  recordRemoval(src);
                }
              }
            }
          } catch (err) { /* never let observer errors break the page */ }
        });
        observer.observe(document.documentElement, {
          childList: true,
          subtree: true
        });
        // Final sweep + disconnect on `load` (not DOMContentLoaded — Smile's
        // runtime can run later than DCL). Even after disconnect, the
        // appendChild/insertBefore overrides below remain active.
        var disconnected = false;
        function lateSweepAndDisconnect() {
          if (disconnected) return;
          disconnected = true;
          try { sweep(); } catch (e) { /* shrug */ }
          try { observer.disconnect(); } catch (e) { /* shrug */ }
        }
        if (document.readyState === 'complete') {
          setTimeout(lateSweepAndDisconnect, 1500);
        } else {
          window.addEventListener('load', function () {
            setTimeout(lateSweepAndDisconnect, 1500);
          }, { once: true });
        }
      } catch (e) { /* observer setup failed — fall through */ }
    }

    // ----- appendChild / insertBefore guards: catch JS-injected scripts -----
    // These guard against `smile-loader.js` (or any other code) injecting
    // Smile runtime scripts via `head.appendChild(<script>)`. The HTML
    // parser does NOT use these methods for source-HTML scripts (it uses
    // internal insertion), so the parser-injected smile-loader.js is
    // caught by the MutationObserver above; the *runtime* scripts that
    // smile-loader.js would then inject are caught here. Belt-and-suspenders.
    try {
      var origAppend = Node.prototype.appendChild;
      var origInsert = Node.prototype.insertBefore;
      Node.prototype.appendChild = function (newNode) {
        try {
          if (isSmileScript(newNode)) {
            recordBlock(newNode.src);
            // Return the node so the caller's chain doesn't break, but
            // never actually attach it to the tree. The script never
            // becomes "in a document", so it never executes.
            return newNode;
          }
        } catch (e) { /* fall through to original */ }
        return origAppend.apply(this, arguments);
      };
      Node.prototype.insertBefore = function (newNode) {
        try {
          if (isSmileScript(newNode)) {
            recordBlock(newNode.src);
            return newNode;
          }
        } catch (e) { /* fall through to original */ }
        return origInsert.apply(this, arguments);
      };
    } catch (e) { /* couldn't install guards — observer alone will have to do */ }

    // Run a sweep right now (in case any Smile scripts were already in
    // DOM before our script ran — unlikely given we load before
    // content_for_header, but cheap to be safe).
    sweep();

    // Schedule additional sweeps at later milestones to catch anything
    // the parser injected after our initial sweep.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        try { setTimeout(sweep, 0); } catch (e) { /* shrug */ }
      });
    }
  } catch (err) {
    // Top-level catch: bail silently rather than break the page.
    if (window.console && console.warn) {
      console.warn('[smile-facade] init error (non-fatal):', err);
    }
  }
})();
